export type OrderStatusFilter =
  | 'all'
  | 'awaiting-payment'
  | 'awaiting-fulfillment'
  | 'awaiting-shipment'
  | 'high-risk'
  | 'pre-order'
  | 'completed'
  | 'shipped'
  | 'refunded'
  | 'cancelled'
  | 'pending'
  | 'awaiting-pickup'
  | 'declined'
  | 'disputed'
  | 'partially-refunded'
  | 'partially-shipped'
  | 'incomplete';

export interface OrderStatusTab {
  id: OrderStatusFilter;
  label: string;
}

/** Primary tabs shown inline — mirrors BigCommerce admin order views. */
export const ORDER_STATUS_PRIMARY_TABS: OrderStatusTab[] = [
  { id: 'all', label: 'All orders' },
  { id: 'awaiting-payment', label: 'Awaiting Payment' },
  { id: 'awaiting-fulfillment', label: 'Awaiting Fulfillment' },
  { id: 'awaiting-shipment', label: 'Awaiting Shipment' },
  { id: 'high-risk', label: 'High Risk' },
  { id: 'pre-order', label: 'Pre-orders' },
];

/** Additional statuses available from the "More" dropdown. */
export const ORDER_STATUS_MORE_TABS: OrderStatusTab[] = [
  { id: 'completed', label: 'Completed' },
  { id: 'shipped', label: 'Shipped' },
  { id: 'refunded', label: 'Refunded' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'pending', label: 'Pending' },
  { id: 'awaiting-pickup', label: 'Awaiting Pickup' },
  { id: 'declined', label: 'Declined' },
  { id: 'disputed', label: 'Disputed' },
  { id: 'partially-refunded', label: 'Partially Refunded' },
  { id: 'partially-shipped', label: 'Partially Shipped' },
  { id: 'incomplete', label: 'Incomplete' },
];

export function getOrderStatusTabLabel(filter: OrderStatusFilter): string {
  const tab = [...ORDER_STATUS_PRIMARY_TABS, ...ORDER_STATUS_MORE_TABS].find((item) => item.id === filter);
  return tab?.label ?? 'All orders';
}

export function isMoreStatusFilter(filter: OrderStatusFilter): boolean {
  return ORDER_STATUS_MORE_TABS.some((tab) => tab.id === filter);
}

const VALID_STATUS_FILTERS = new Set<OrderStatusFilter>([
  'all',
  ...ORDER_STATUS_PRIMARY_TABS.map((tab) => tab.id),
  ...ORDER_STATUS_MORE_TABS.map((tab) => tab.id),
]);

/** Read status filter from the orders list query string. */
export function parseOrderStatusFilterFromSearch(search: string): OrderStatusFilter {
  const value = new URLSearchParams(search).get('status');
  if (value && VALID_STATUS_FILTERS.has(value as OrderStatusFilter)) {
    return value as OrderStatusFilter;
  }
  return 'all';
}

/** Build orders list path preserving the active status tab. */
export function buildOrdersListPath(status: OrderStatusFilter = 'all'): string {
  return status === 'all' ? '/orders' : `/orders?status=${encodeURIComponent(status)}`;
}
